import { Flex } from 'antd';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import React, { memo, MouseEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { ApplicationRootState } from 'types';
import { setSelectedLocation } from '../../actions';
import Navigation from '../../images/navigation.svg';
import messages from '../../messages';
import { getSelectedLocation, selectCoords } from '../../selectors';
import { Coords, LocationByDistrict } from '../../type';
import { useMapContainerContext } from '../Map/hooks/MapContainerContext';
import { customEncodeURIComponent } from '../Map/hooks/useConvertPlusCodeToCoords';

const LocationCardContainer = styled(Flex)<{ selected: boolean }>`
  cursor: ${(props) => (props.selected ? 'default' : 'pointer')};
  display: flex;
  padding: 14px 16px;
  align-items: center;
  text-wrap: balance;
  gap: 6px;
  &[aria-selected='true'] {
    background: #f0f9f1;
    color: #475467;
  }
  & > *:first-child {
    flex: 2;
  }
  & > *:last-child {
    flex: 1;
  }
  div {
    & > address,
    & > p {
      font-size: 14px;
      font-style: normal;
      color: #2f382c;
      font-weight: 400;
    }
  }
  &:hover {
    background: #f0f9f1;
  }
`;

const NavigationLink = styled.a`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  color: #136fd8;
  font-size: 12px;
  white-space: nowrap;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  &:hover {
    color: #43a047;
  }
`;

const NavigationIcon = styled(SVGIcon)`
  height: 42px;
  width: 42px;
  aspect-ratio: 1/1;
  padding: 4px;
  border-radius: 50%;
  border: 1px solid #136fd8;
  background: #fff;
  & svg {
    stroke: #136fd8;
    width: 20px;
  }

  ${NavigationLink}:hover & {
    border-color: #3c9041;
    svg {
      stroke: #3c9041;
    }
  }

  ${NavigationLink}:active & {
    border-color: #3c9041;
    svg {
      stroke: #2f7434;
    }
  }
`;

interface IStateProps {
  selectedLocation: string;
  currentCoords: Coords;
}

interface IDispatchProps {
  setSelectedLocation: (location: string) => void;
}

type Props = IStateProps &
  IDispatchProps & {
    location: LocationByDistrict;
    lang: string;
    element: React.RefObject<HTMLDivElement>;
  };

const LocationCard: React.FC<Props> = ({
  selectedLocation,
  // currentCoords,
  setSelectedLocation,
  location,
  lang,
  element,
}) => {
  const context = useMapContainerContext();
  const { setOpenCardInfo } = context;
  if (location.id === selectedLocation) {
    element?.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }

  const onLocationTabClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setSelectedLocation(location.id);
    const locationOnMap = {
      id: location.id,
      name: location[`name_${lang}`],
      address: location[`addr_${lang}`],
      district: location[`district_${lang}`],
      tel: location?.tel || location?.tel?.toLowerCase() === 'nothing' ? '' : location.tel,
      close_time: location.close_time,
      open_time: location.open_time,
      coords: {
        lat: location.geo_long ? parseFloat(location.geo_long) : 0,
        lng: location.geo_lat ? parseFloat(location.geo_lat) : 0,
      },
      plus_code: location.plus_code,
    };
    setOpenCardInfo(locationOnMap);
  };

  return (
    <LocationCardContainer
      align="center"
      justify="space-between"
      gap="middle"
      key={location.id}
      onClick={(e: MouseEvent<HTMLDivElement>) => {
        if (location.id === selectedLocation) {
          return;
        }
        onLocationTabClick(e);
      }}
      selected={location.id === selectedLocation}
      aria-selected={location.id === selectedLocation}
    >
      <div>
        <strong>{location[`name_${lang}`]}</strong>
        <address>{location[`addr_${lang}`]}</address>
        {location.close_time && (
          <p>
            <FormattedMessage {...messages.closeTime} />: {location.close_time}
          </p>
        )}
        {location.open_time && (
          <p>
            <FormattedMessage {...messages.openTime} />: {location.open_time}
          </p>
        )}
        {location.tel && (
          <p>
            <FormattedMessage {...messages.tel} />: {location.tel}
          </p>
        )}
      </div>
      <NavigationLink
        href={`https://www.google.com/maps/${location.plus_code ? `place/${customEncodeURIComponent(location.plus_code)}` : `dir//${location.geo_lat},${location.geo_long}`}/`}
        target="_blank"
      >
        <NavigationIcon src={Navigation} renderAsImg={false} />
        <p>
          <FormattedMessage {...messages.getDirection} />
        </p>
      </NavigationLink>
    </LocationCardContainer>
  );
};

const mapStateToProps = createStructuredSelector<ApplicationRootState, IStateProps>({
  selectedLocation: getSelectedLocation(),
  currentCoords: selectCoords(),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setSelectedLocation: (id: string) => dispatch(setSelectedLocation(id)),
});

const LocationCardWrapper = connect(mapStateToProps, mapDispatchToProps)(LocationCard);

export default memo(LocationCardWrapper);
